/**
 * Studio project reads and the cross-domain project operations.
 *
 * The store holds projects and applies single-domain mutations. This hook is where the
 * operations touching more than one domain live, which is the division of labour stated
 * in hooks/index.ts:
 *
 *   - naming a project toasts (UI)
 *   - a project's songs are resolved against the LIBRARY
 *   - deleting a song has to leave every project (SONGS → PROJECTS)
 *
 * A project resolves its ids against the library on read (projectSongs), so nothing here
 * synchronises anything: renaming or re-covering a song is already reflected wherever it
 * appears. The one thing that cannot be handled on read is deletion, hence
 * removeSongEverywhere, called from useSongs.deleteSong — the single funnel every delete
 * goes through.
 *
 * NOT SIMULATED in the sense the Studio's AI tools are. A project does exactly what it
 * says: it is a name and the songs the user chose, and opening one really does open the
 * workspace on them. The only thing missing is persistence, because studioProjectsApi is
 * a mock — see its header, and note that §3 asks specifically that this not be papered
 * over, so the screen states it too.
 */
import { useMemo } from 'react';
import { projectNameError } from '../api/studioProjectsApi';
import {
  projectSongs,
  projectSummary,
  selectProjectById,
  useStudioProjectsStore,
} from '../stores/studioProjectsStore';
import { useSongsStore } from '../stores/songsStore';
import { useUiStore } from '../stores/uiStore';
import type { ProjectSongScope, Song, StudioProject } from '../types/domain';

export function useStudioProjects() {
  // One field per selector: Zustand v5 compares with Object.is, so an object literal
  // here would return a new reference on every unrelated store change.
  const projects = useStudioProjectsStore((s) => s.projects);
  const activeProjectId = useStudioProjectsStore((s) => s.activeProjectId);
  const createProjectRaw = useStudioProjectsStore((s) => s.createProject);
  const renameProjectRaw = useStudioProjectsStore((s) => s.renameProject);
  const deleteProjectRaw = useStudioProjectsStore((s) => s.deleteProject);
  const addSongRaw = useStudioProjectsStore((s) => s.addSongToProject);
  const removeSongRaw = useStudioProjectsStore((s) => s.removeSongFromProject);
  const setActiveProject = useStudioProjectsStore((s) => s.setActiveProject);

  const songs = useSongsStore((s) => s.songs);
  const showToast = useUiStore((s) => s.showToast);

  /*
    Sorted newest-first, as the library and the playlists are. Memoised on the array
    rather than sorted in the store, because sorting on write would make the stored order
    a second ordering to keep in step with `seq` — and `seq` is the one that means
    something.
  */
  const sortedProjects = useMemo(() => [...projects].sort((a, b) => b.seq - a.seq), [projects]);

  const activeProject = selectProjectById(projects, activeProjectId);

  return {
    projects: sortedProjects,
    activeProject,
    setActiveProject,

    /** Looks up a project by id, for route params. */
    getProject(id: string | undefined): StudioProject | null {
      return selectProjectById(projects, id ?? null);
    },

    /** The project's songs, in the order they were added. Unresolvable ids are dropped. */
    songsIn(project: StudioProject): Song[] {
      return projectSongs(project, songs);
    },

    /** "3 tracks · whole library". */
    summaryFor(project: StudioProject): string {
      return projectSummary(project, songs);
    },

    /**
     * Why a name would be refused, or null. The form's own copy of the rule.
     *
     * Re-exported through this hook rather than imported from the API by every screen,
     * so a component asks its feature hook and does not reach into the API layer — and
     * so there is still exactly ONE rule, in api/studioProjectsApi.
     */
    nameError: projectNameError,

    /**
     * Creates a project and makes it the active one, returning it so the caller can open
     * the workspace.
     *
     * REFUSES A BAD NAME through the same `projectNameError` the API enforces, so the
     * refusal a user sees and the refusal a server would give are one rule rather than
     * two that drift. Returns null when refused; the caller shows the message.
     *
     * `songIds` come from the library the user already owns — nothing is created, copied
     * or stored here beyond the references.
     */
    createProject(name: string, songIds: string[], scope: ProjectSongScope): StudioProject | null {
      const trimmed = name.trim();
      if (projectNameError(trimmed)) return null;
      const project = createProjectRaw(trimmed, songIds, scope);
      setActiveProject(project.id);
      showToast(`Project "${trimmed}" created`);
      return project;
    },

    /** Renames a project. An emptied name is refused, as on create. */
    renameProject(project: StudioProject, name: string) {
      const trimmed = name.trim();
      if (projectNameError(trimmed) || trimmed === project.name) return;
      renameProjectRaw(project.id, trimmed);
      showToast(`Renamed to "${trimmed}"`);
    },

    /**
     * Deletes a project.
     *
     * The songs are untouched — a project is a view of the library, not a container that
     * owns what is in it. Said in the toast for the reason usePlaylists.deletePlaylist
     * says it: "delete" on a screen full of tracks is worth being unambiguous about.
     */
    deleteProject(project: StudioProject) {
      deleteProjectRaw(project.id);
      showToast(`Project "${project.name}" deleted — your songs are still in your library`);
    },

    /** Adds a song, toasting either way so a duplicate press is not silent. */
    addSong(project: StudioProject, song: Song) {
      if (project.songIds.includes(song.id)) {
        showToast(`"${song.title}" is already in "${project.name}"`);
        return;
      }
      addSongRaw(project.id, song.id);
      showToast(`Added "${song.title}" to "${project.name}"`);
    },

    /**
     * Removes a song from a project.
     *
     * Nothing is done to the player, unlike usePlaylists.removeSong: a project has no
     * queue, because it is not a listening sequence. What is playing is left alone.
     */
    removeSong(project: StudioProject, song: Song) {
      removeSongRaw(project.id, song.id);
      showToast(`Removed "${song.title}" from "${project.name}"`);
    },
  };
}
